// Sayfa başına VURGU RENGİ — yükleme efekti (PageLoader) açılacak sayfanın
// tasarım diliyle aynı renkte belirsin diye.
//
// Değerler, o sayfanın İÇERİK bileşenindeki ACCENT sabitiyle eşleşir (kullanıcı
// yüklemeden sonra o rengi göreceği için). Bilerek KÜÇÜK ve bağımsız bir
// modüldür: PageLoader main.js'e dahildir, buradan program verisine ya da
// ProgramCard'a bağlanmak o ağır modülleri de main'e sürüklerdi.
const SITE_ACCENT = '#3d84ff';

// /program/<slug> → vurgu
const PROGRAM_ACCENTS: Record<string, string> = {
  'terminal-call': '#fe6a00', // TerminalContent
  'next-level': '#fd6a00', // NextLevelContent
  'rota-tech-development': '#6f76d8', // RotaContent
  'tasting-workshops': '#f5a524', // TastingContent
  'defense-industry-development': '#4e9bda', // KapsamContent
  engineerhub: '#ff8500', // EngineerhubContent
  bistlab: '#0093ac', // BistlabContent
  'water-hackathon': '#22b8d4', // WaterHackathon
  'agriculture-ideathon': '#d79328', // AgriIdeathon
  'agriculture-technologies': '#d79328', // AgriTechContent
  'agriculture-workshop': '#8dc63f', // AgriWorkshop
  'real-estate-ideathon': '#005dad', // RealEstateIdeathon
  'prototype-acceleration': '#ffb600', // PrototypeContent
  'mentor-pool': '#3d84ff', // MentorContent
  // Tamamlanan Projeler temaları (ProgramDetail PROJECT_THEMES)
  academy: '#f2b53c',
  'smart-city': '#38bdf8',
  'project-capacity-development': '#8b7cf8',
  entrepreneurship: '#46c68c',
};

// Sabit rotalar → vurgu
const ROUTE_ACCENTS: Record<string, string> = {
  '/': SITE_ACCENT, // Home
  '/institutional': SITE_ACCENT, // Corporate
  '/radar': '#7ea6ff', // Radar
  '/community': '#46c68c', // Eklem (CommunityIntro)
  '/mentorship': '#3d84ff',
  '/technical-visit': '#2dd4bf', // TechnicalVisitBackdrop
};

// Çok koyu vurgular (ör. #005dad) koyu sayfa zemininde kaybolur; yükleyici
// görünür kalsın diye asgari parlaklığa çekilir. Ton (hue) korunur.
const ensureVisible = (hex: string): string => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return SITE_ACCENT;
  const n = parseInt(m[1], 16);
  let r = (n >> 16) & 255;
  let g = (n >> 8) & 255;
  let b = n & 255;
  // Algısal parlaklık (ITU-R BT.601)
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const MIN = 0.42;
  if (lum >= MIN) return `#${m[1].toLowerCase()}`;
  const k = MIN / Math.max(lum, 0.01);
  const clamp255 = (v: number) => Math.min(255, Math.round(v * k));
  r = clamp255(r);
  g = clamp255(g);
  b = clamp255(b);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

/** Bir programın (slug) vurgu rengi — başvuru FORMU sayfası bu temayla çizilir.
 * Bilinmeyen/boş slug'da site varsayılanı (SITE_ACCENT). */
export const accentForProgram = (slug?: string): string =>
  ensureVisible((slug && PROGRAM_ACCENTS[slug]) || SITE_ACCENT);

// ── SAYFA ZEMİN RENGİ ───────────────────────────────────────────────────────
// Sayfaların kendi koyu zemini. Bu değerler ROTADAN türetilebilir olmalı çünkü
// `usePageBackground` çağrıları lazy sayfa bileşenlerinin İÇİNDE: chunk gelene
// kadar --page-bg varsayılanda kalıyor, yükleme ekranı lacivert (#03060f)
// görünüp sayfa gelince ör. siyaha (#070608) atlıyordu. SiteLayout bu haritayı
// kullanarak zemini ROTA DEĞİŞİR DEĞİŞMEZ uygular.
export const DEFAULT_PAGE_BG = '#03060f';

/** /program/<slug> → zemin. ProgramDetail de bunu kullanır (tek kaynak). */
export const PROGRAM_PAGE_BG: Record<string, string> = {
  'agriculture-ideathon': '#080704',
  'agriculture-technologies': '#080704',
  'agriculture-workshop': '#050a05',
  bistlab: '#04090c',
  'defense-industry-development': '#040f1a',
  engineerhub: '#060607',
  'mentor-pool': '#03060f',
  'next-level': '#070607',
  'prototype-acceleration': '#070605',
  'real-estate-ideathon': '#02060f',
  'rota-tech-development': '#05060b',
  'tasting-workshops': '#070605',
  'terminal-call': '#070608',
  'water-hackathon': '#030a10',
};

// Sabit rotalar → zemin (sayfaların usePageBackground / kök bg değerleriyle
// birebir aynı olmalı).
const ROUTE_PAGE_BG: Record<string, string> = {
  '/radar': '#060506', // Radar.tsx
  '/community': '#05070f', // CommunityIntro.tsx
  '/mentorship': '#03060f', // forms/Mentorship.tsx
};

/** Verilen rotanın zemin rengi (bilinmeyen rotalarda site varsayılanı). */
export const backgroundForPath = (pathname: string): string => {
  const path = pathname.replace(/\/+$/, '') || '/';
  const fixed = ROUTE_PAGE_BG[path];
  if (fixed) return fixed;
  const program = /^\/program\/([a-z0-9-]+)$/.exec(path);
  const byProgram = program ? PROGRAM_PAGE_BG[program[1]] : undefined;
  return byProgram ?? DEFAULT_PAGE_BG;
};

/** Verilen rotanın vurgu rengi (bilinmeyen rotalarda site mavisi). */
export const accentForPath = (pathname: string): string => {
  const path = pathname.replace(/\/+$/, '') || '/';
  const fixed = ROUTE_ACCENTS[path];
  if (fixed) return ensureVisible(fixed);
  const program = /^\/program\/([a-z0-9-]+)$/.exec(path);
  const byProgram = program ? PROGRAM_ACCENTS[program[1]] : undefined;
  return ensureVisible(byProgram ?? SITE_ACCENT);
};

export default accentForPath;
